import client from "@/api/openapi-ts";
import { fetchExportDownload } from "@/lib/exports/downloadFile";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, computed } from "vue";
import { Geography } from "@/types/api/Geography";
import { MetaAudience } from "@/types/api/MetaAudience";
import { PartnerCut } from "@/types/api/PartnerCut";
import { useGeographyStore } from "@/stores/GeographyStore";
import { useMetaAudienceStore } from "@/stores/MetaAudienceStore";
import { geoTypes, getGeoType } from "@/helpers/geoTypes";
import { UpdatedDemographicResponse } from "@/types/api/Demographic";
import { usePartnerCutStore } from "@/stores/PartnerCutStore";
import { usePlugins } from "./helpers/usePlugins";
import { useExports } from "./helpers/useExports";
import { useTaskStore } from "@/stores/TaskStore";
import { toTitleCase } from "@/stores/helpers/helpers";
import { usersAllowedHaloAudiences } from "@/helpers/usersAllowedHaloAudiences";
import type { components } from "@/api/schema.d.ts";

type schemas = components["schemas"];

const MEDIUMS_MAPS = {
  media_category: "Media Categories",
  stream: "TV Streaming",
  social: "Social Media",
  traditional_radio: "Traditional Radio",
  streaming_audio: "Streaming Audio",
  news_media: "News Media",
};

type Loading = {
  audienceCounts: boolean;
  metaAudience: boolean;
  geography: boolean;
  partnerCuts: boolean;
  demographics: boolean;
  mediaMix: boolean;
  mediaMixSelections: boolean;
  consumerInsights: boolean;
  marketRanker: boolean;
  policyInsights: boolean;
  topTvPrograms: boolean;
  checkHouseholdThreshold: boolean;
  linearStreaming: boolean;
  behavioralInsights: boolean;
  haloInsights: boolean;
  activationSummary: boolean;
  aiPersona: boolean;
};

type Errors = {
  audienceCounts: string | undefined;
  demographics: string | undefined;
  consumerInsights: string | undefined;
  policyInsights: string | undefined;
  marketRanker: string | undefined;
  linearStreaming: string | undefined;
  behavioralInsights: string | undefined;
  mediaMix: string | undefined;
  haloInsights: string | undefined;
  topTvPrograms: string | undefined;
};

type MarketRanker = {
  name: string;
  density: number;
  index_audience_population: number;
  audience_population: number;
};

type MediaMixMedium = schemas["AudienceExplorerMedium"] & {
  audience_coverage: number;
};

type MediaMixOverview = schemas["AudienceExplorerMediumOverview"] & {
  audience_coverage: number;
};

type MediaMixFilterKey = "all" | "political_buying" | "ad_supported";

type MediaMixFilterOverviews = Record<MediaMixFilterKey, MediaMixOverview>;

/** Processed media mix stored in the store (display category keys + audience_coverage). */
type MediaMix = {
  categories: Record<string, MediaMixMedium[]>;
  overallByCategory: Record<string, MediaMixFilterOverviews>;
  selectedByCategory: Record<string, MediaMixOverview>;
  overall: schemas["AudienceExplorerMediumOverview"];
};

type AudienceCountsResponse = {
  count: number;
  geo_count: number;
};

type LinearStreamingResponse = {
  meta_audience_ids: {
    high_streaming: number;
    low_streaming: number;
    high_linear: number;
    low_linear: number;
  };
  audience: {
    high_streaming_low_linear: number;
    high_streaming_high_linear: number;
    low_streaming_low_linear: number;
    low_streaming_high_linear: number;
  };
  index_audience: {
    high_streaming_low_linear: number;
    high_streaming_high_linear: number;
    low_streaming_low_linear: number;
    low_streaming_high_linear: number;
  };
};

type BehavioralInsights =
  components["schemas"]["AudienceExplorerBehavioralInsights"];

type HaloInsights = components["schemas"]["AudienceExplorerHaloInsightsCount"];

type PendingTaskResponse = {
  task_id: string;
};

type ActivationSummaryResponse = {
  count: number;
  geo_count: number;
  household_count: number;
  cell_phone_count: number;
};

type SampleRatioResponse = {
  sample_ratio: number;
};

// Geography-count CSV exports run one task per selected geography level. These
// are the geography types the download menu can request (all of `geoTypes`
// except national + zip_codes), and they double as the per-export keys for the
// shared export manager. `jurisnames` isn't a `geoTypes` entry but some states
// return it from the cities endpoint, and the download menu + API both accept it
// as a `geography_type`, so it must be registered here too or its completion
// message never matches and the export stays stuck loading.
const GEOGRAPHY_EXPORT_TYPES = [
  "states",
  "dmas",
  "dma_states",
  "counties",
  "cities",
  "jurisnames",
  "congressional_district_nextelections",
  "senate_district_nextelections",
  "legislative_district_nextelections",
] as const;

type GeographyExportType = (typeof GEOGRAPHY_EXPORT_TYPES)[number];
type ExportKey = "export" | GeographyExportType;

const PREVIEW_ERROR_KEYS = [
  "audienceCounts",
  "demographics",
  "consumerInsights",
  "policyInsights",
  "marketRanker",
  "linearStreaming",
  "behavioralInsights",
  "mediaMix",
] as const satisfies readonly (keyof Errors)[];

export const useAudienceExplorerSearchStore = defineStore(
  "audienceExplorerSearch",
  () => {
    const { $ws } = usePlugins();
    const taskStore = useTaskStore();
    const cancel = async (key: keyof typeof taskIds.value) => {
      if (taskIds.value[key]) {
        await taskStore.cancelTask(taskIds.value[key]);
        taskIds.value[key] = undefined;
      }
    };
    const initializing = ref(false);
    const loading = ref<Loading>({
      audienceCounts: false,
      metaAudience: false,
      geography: false,
      partnerCuts: false,
      demographics: false,
      mediaMix: false,
      mediaMixSelections: false,
      consumerInsights: false,
      marketRanker: false,
      policyInsights: false,
      topTvPrograms: false,
      checkHouseholdThreshold: false,
      linearStreaming: false,
      behavioralInsights: false,
      haloInsights: false,
      activationSummary: false,
      aiPersona: false,
    });
    const errors = ref<Errors>({
      audienceCounts: undefined,
      demographics: undefined,
      consumerInsights: undefined,
      policyInsights: undefined,
      marketRanker: undefined,
      linearStreaming: undefined,
      behavioralInsights: undefined,
      mediaMix: undefined,
      haloInsights: undefined,
      topTvPrograms: undefined,
    });
    const taskIds = ref<{
      audienceCounts: string | undefined;
      mediaMix: string | undefined;
      demographics: string | undefined;
      marketRanker: string | undefined;
      topTvPrograms: string | undefined;
      linearStreaming: string | undefined;
      behavioralInsights: string | undefined;
      haloInsights: string | undefined;
      policyInsights: string | undefined;
      consumerInsights: string | undefined;
      activationSummary: string | undefined;
      aiPersona: string | undefined;
    }>({
      audienceCounts: undefined,
      mediaMix: undefined,
      demographics: undefined,
      marketRanker: undefined,
      topTvPrograms: undefined,
      linearStreaming: undefined,
      behavioralInsights: undefined,
      haloInsights: undefined,
      policyInsights: undefined,
      consumerInsights: undefined,
      activationSummary: undefined,
      aiPersona: undefined,
    });
    const error = ref<string | null>(null);
    const defaultMetaAudienceId = ref<number | null>(null);
    const metaAudience = ref<MetaAudience | null>(null);
    const geography = ref<Geography | null>(null);
    const partnerCuts = ref<PartnerCut[]>([]);
    const defaultIndexAudienceId = ref<number | null>(null);
    const comparisonAudience = ref<MetaAudience | null>(null);
    const comparisonParam = computed(() =>
      comparisonAudience.value?.id
        ? { index_meta_audience_id: comparisonAudience.value.id }
        : {},
    );
    const defaultPopulationLabel = computed(() =>
      audienceSource.value === "voters"
        ? "Voter Population"
        : "General Population",
    );
    const comparisonLabel = computed(
      () => comparisonAudience.value?.name ?? defaultPopulationLabel.value,
    );
    const sampleRatio = ref<number | null>(null);
    const audienceCounts = ref<AudienceCountsResponse | null>(null);
    const demographics = ref<UpdatedDemographicResponse | null>();
    const consumerInsights = ref<any>();
    const policyInsights = ref<any>();
    const marketRanker = ref<MarketRanker[] | undefined>(undefined);
    const mediaMix = ref<MediaMix | null>(null);
    const tvRatingsError = ref<string | null>(null);
    const topTvPrograms = ref<any[]>([]);
    const linearStreaming = ref<LinearStreamingResponse | null>(null);
    const behavioralInsights = ref<BehavioralInsights | null>(null);
    const haloInsights = ref<HaloInsights[]>([]);
    const exportError = ref<string | null>(null);
    const activationSummary = ref<ActivationSummaryResponse | null>(null);
    const aiPersona = ref<string | null>(null);
    const hideComponentsForError = ref<boolean>(false);
    const comparisonIncludedInExplore = ref(false);

    // Shared export workflow for the PPTX report ("export") and the per-geography
    // CSV count exports. Owns each export's loading/task state, the websocket
    // subscription, and the file download once the task completes.
    const exportManager = useExports<ExportKey>({
      channel: "AudienceExplorer",
      keys: ["export", ...GEOGRAPHY_EXPORT_TYPES],
      fetchFile: (file, signal) =>
        fetchExportDownload(
          "/api/v1/audience_explorer/export/{filename}",
          file,
          signal,
        ),
      // The report-export error surfaces through the page's `exportError` banner;
      // count-export failures stay in the manager's per-key `errors` record.
      onError: (key, message) => {
        if (key === "export") exportError.value = message;
      },
    });

    const geographyType = computed(() => {
      const type = getGeoType(geography.value?.type ?? "");
      return geoTypes.find((t) => t.type === type)?.value;
    });
    const parsedGeography = computed(() => {
      if (!geography.value) return "";
      const geoType = geoTypes.find(
        (geo) => geo.type === getGeoType(geography.value?.type ?? ""),
      );
      if (!geoType) return "";
      const geographyTypeText = geoType.text;
      if (geography.value?.type === "national") {
        return geographyTypeText;
      } else {
        return `${geographyTypeText}: ${prettifyGeo.value}`;
      }
    });
    const prettifyGeo = computed(() => {
      if (!geography.value) return;
      const geographyStore = useGeographyStore();
      const state = geographyStore.states.find(
        (state) => state.state_id === geography.value?.state_id,
      );
      switch (getGeoType(geography.value.type)) {
        case "counties":
          return `${geography.value.name} ${geography.value.descriptor}, ${state?.name}`;
        case "cities":
          return `${toTitleCase(geography.value.name)}, ${state?.name}`;
        case "congressional_district_nextelections":
          return `${state?.name} - CD ${geography.value.name}`;
        case "senate_district_nextelections":
          return `${state?.name} - State Senate ${geography.value.name}`;
        case "legislative_district_nextelections":
          return `${state?.name} - State Legislature ${geography.value.name}`;
        case "national":
          return "National";
        default:
          return geography.value.name;
      }
    });
    const audienceSource = computed(() => {
      if (!partnerCuts.value) return;
      const standardCut = partnerCuts.value.find((partnerCut) => {
        return partnerCut.partner?.name === "Standard";
      });
      return standardCut?.audience?.audience_source?.label;
    });
    const allPreviewTasksFailed = computed(() =>
      PREVIEW_ERROR_KEYS.every(
        (key) => !loading.value[key] && errors.value[key] != null,
      ),
    );

    return {
      initializing,
      loading,
      errors,
      allPreviewTasksFailed,
      taskIds,
      // Per-export loading record from the shared manager: `export` is the PPTX
      // report; the geography-type keys are the CSV count exports.
      exportLoading: exportManager.loading,
      handleExportMessage: exportManager.handleMessage,
      error,
      hideComponentsForError,
      defaultMetaAudienceId,
      metaAudience,
      geography,
      partnerCuts,
      defaultIndexAudienceId,
      comparisonAudience,
      comparisonIncludedInExplore,
      defaultPopulationLabel,
      comparisonLabel,
      audienceCounts,
      sampleRatio,
      demographics,
      consumerInsights,
      marketRanker,
      policyInsights,
      mediaMix,
      tvRatingsError,
      topTvPrograms,
      linearStreaming,
      geographyType,
      parsedGeography,
      prettifyGeo,
      audienceSource,
      behavioralInsights,
      haloInsights,
      exportError,
      activationSummary,
      aiPersona,
      reset(afterError: boolean = false) {
        if (comparisonAudience.value?.id === metaAudience.value?.id) {
          comparisonAudience.value = null;
        }
        comparisonIncludedInExplore.value = comparisonAudience.value !== null;
        error.value = null;
        // clear out all Errors in PREVIEW_ERROR_KEYS
        PREVIEW_ERROR_KEYS.forEach((key) => {
          errors.value[key] = undefined;
        });
        if (!afterError) {
          // we want to hide all the data components after an error
          hideComponentsForError.value = false;
          audienceCounts.value = null;
        }
        demographics.value = null;
        consumerInsights.value = undefined;
        policyInsights.value = undefined;
        marketRanker.value = undefined;
        mediaMix.value = null;
        tvRatingsError.value = null;
        topTvPrograms.value = [];
        linearStreaming.value = null;
        behavioralInsights.value = null;
        haloInsights.value = [];
        exportError.value = null;
        activationSummary.value = null;
        aiPersona.value = null;
      },
      async refetchComparisonData() {
        comparisonIncludedInExplore.value = false;
        if (!metaAudience.value || !geography.value || !audienceCounts.value)
          return;
        demographics.value = null;
        consumerInsights.value = undefined;
        policyInsights.value = undefined;
        marketRanker.value = undefined;
        mediaMix.value = null;
        linearStreaming.value = null;
        behavioralInsights.value = null;
        this.getDemographics();
        this.getCategoryInsights("Consumer");
        this.getCategoryInsights("Advocacy");
        this.getMarketRanker();
        this.getLinearStreaming();
        this.getMediaMix();
        this.getBehavioralInsights();
      },
      async loadDefaultAudience(): Promise<boolean> {
        const { data, response } = await client.GET(
          "/api/v1/audience_explorer/audience",
        );
        if (response.status === 401) {
          defaultMetaAudienceId.value = null;
          return false;
        }
        if (!response.ok) {
          defaultMetaAudienceId.value = null;
          error.value =
            "Unable to load default audience. Please try again or select an audience.";
          return false;
        }
        const id = (data as { id?: number } | undefined)?.id ?? null;
        defaultMetaAudienceId.value = id;
        if (id === null) {
          error.value = "Default audience not found.";
          return false;
        }
        return true;
      },
      async loadMetaAudience(id: number) {
        const metaAudienceStore = useMetaAudienceStore();
        loading.value.metaAudience = true;
        try {
          const fields = [
            "audience_issues",
            "audience_category",
            "audience_category_id",
            "audience_topic",
            "audience_topic_id",
            "id",
            "long_description",
            "name",
            "refreshed",
            "states",
            "uses_fec_donor_data",
          ];
          metaAudience.value = await metaAudienceStore.get(id, fields);
        } catch {
          error.value = "Audience not found";
        } finally {
          loading.value.metaAudience = false;
        }
      },

      async loadGeography(_id: number) {
        const geographyStore = useGeographyStore();
        loading.value.geography = true;
        try {
          geography.value = await geographyStore.get(_id);
        } catch {
          error.value = "Geography not found";
        } finally {
          loading.value.geography = false;
        }
      },
      async loadPartnerCuts() {
        if (!metaAudience.value?.id) return;
        loading.value.partnerCuts = true;
        const partnerCutStore = usePartnerCutStore();
        try {
          partnerCuts.value = await partnerCutStore.get(metaAudience.value.id);
        } finally {
          loading.value.partnerCuts = false;
        }
      },
      async getSampleRatio() {
        if (!metaAudience.value) return;
        const { data, error } = await client.GET(
          "/api/v1/audience_explorer/{meta_audience_id}/sample_ratio",
          {
            params: { path: { meta_audience_id: metaAudience.value.id! } },
          },
        );
        if (error) return;
        sampleRatio.value =
          (data as SampleRatioResponse | undefined)?.sample_ratio ?? null;
      },
      async getAudienceCounts() {
        if (!metaAudience.value || !geography.value) return;
        await cancel("audienceCounts");
        loading.value.audienceCounts = true;
        errors.value.audienceCounts = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/counts",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value.audienceCounts = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.audienceCounts = false;
          }
        } catch {
          loading.value.audienceCounts = false;
        }
      },
      async getAiPersona() {
        if (!metaAudience.value || !geography.value) return;
        await cancel("aiPersona");
        loading.value.aiPersona = true;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/ai_persona",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value.aiPersona = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.aiPersona = false;
          }
        } catch {
          loading.value.aiPersona = false;
        }
      },
      async getDemographics() {
        if (!metaAudience.value || !geography.value) return;
        await cancel("demographics");
        loading.value.demographics = true;
        errors.value.demographics = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/demographics",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
                query: { ...comparisonParam.value },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value.demographics = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.demographics = false;
          }
        } catch {
          loading.value.demographics = false;
        }
      },
      async getCategoryInsights(category: string) {
        // category is either Consumer or Advocacy
        if (!metaAudience.value || !geography.value) return;
        const key =
          category === "Advocacy" ? "policyInsights" : "consumerInsights";
        if (key === "consumerInsights") {
          consumerInsights.value = undefined;
          await cancel("consumerInsights");
        } else {
          policyInsights.value = undefined;
          await cancel("policyInsights");
        }
        loading.value[key as keyof Loading] = true;
        errors.value[key as keyof Errors] = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/category_insights/{category}",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                  category,
                },
                query: { ...comparisonParam.value },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value[key as keyof typeof taskIds.value] = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value[key as keyof Loading] = false;
          }
        } catch {
          loading.value[key as keyof Loading] = false;
        }
      },
      async getMarketRanker() {
        if (!metaAudience.value || !geography.value) return;
        await cancel("marketRanker");
        loading.value.marketRanker = true;
        errors.value.marketRanker = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/market_ranker",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
                query: { ...comparisonParam.value },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value.marketRanker = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.marketRanker = false;
          }
        } catch {
          loading.value.marketRanker = false;
        }
      },
      async getLinearStreaming() {
        if (!metaAudience.value || !geography.value) return;
        await cancel("linearStreaming");
        loading.value.linearStreaming = true;
        errors.value.linearStreaming = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/linear_streaming",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
                query: { ...comparisonParam.value },
              },
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value.linearStreaming = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.linearStreaming = false;
          }
        } catch {
          loading.value.linearStreaming = false;
        }
      },
      async getBehavioralInsights() {
        if (!metaAudience.value || !geography.value) return;
        await cancel("behavioralInsights");
        loading.value.behavioralInsights = true;
        errors.value.behavioralInsights = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/behavioral_insights",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
                query: { ...comparisonParam.value },
              },
            },
          );
          const taskId = (data as unknown as PendingTaskResponse | undefined)
            ?.task_id;
          if (taskId) {
            taskIds.value.behavioralInsights = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.behavioralInsights = false;
          }
        } catch {
          loading.value.behavioralInsights = false;
        }
      },
      async getHaloInsights() {
        const haloAudiences = usersAllowedHaloAudiences();
        if (!metaAudience.value || !geography.value || !haloAudiences.length)
          return;
        await cancel("haloInsights");
        loading.value.haloInsights = true;
        errors.value.haloInsights = undefined;
        try {
          const { data } = await client.POST(
            "/api/v1/audience_explorer/halo_insights",
            {
              body: {
                meta_audience_id: metaAudience.value.id,
                geography_id: geography.value.id,
                halo_audience_ids: haloAudiences,
              } as schemas["AudienceExplorerHaloInsightsRequest"],
            },
          );
          const taskId = (data as unknown as PendingTaskResponse | undefined)
            ?.task_id;
          if (taskId) {
            taskIds.value.haloInsights = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.haloInsights = false;
          }
        } catch {
          loading.value.haloInsights = false;
        }
      },
      async getMediaMix(getSelections: boolean = false, mediums?: string[]) {
        if (!metaAudience.value || !geography.value) return;
        await cancel("mediaMix");
        const params = mediums?.length ? { mediums } : {};
        const loadingKey = getSelections ? "mediaMixSelections" : "mediaMix";

        loading.value[loadingKey] = true;
        errors.value.mediaMix = undefined;
        try {
          const { data } = await client.GET(
            "/api/v1/audience_explorer/{meta_audience_id}/{geography_id}/mediums",
            {
              params: {
                path: {
                  meta_audience_id: metaAudience.value.id!,
                  geography_id: geography.value.id!,
                },
                query: { ...params, ...comparisonParam.value } as never,
              },
            },
          );
          const taskId = (data as unknown as PendingTaskResponse | undefined)
            ?.task_id;
          if (taskId) {
            taskIds.value.mediaMix = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value[loadingKey] = false;
          }
        } catch {
          loading.value[loadingKey] = false;
        }
      },
      processMediaMixData(
        data: schemas["AudienceExplorerMediumOverall"],
      ): MediaMix {
        const processedMediaMix: Record<string, MediaMixMedium[]> = {};
        const overallMediaMix: Record<string, MediaMixFilterOverviews> = {};
        const selectedByCategory: Record<string, MediaMixOverview> = {};
        const mapOverview = (
          overview: schemas["AudienceExplorerMediumOverview"],
        ): MediaMixOverview => ({
          ...overview,
          audience_coverage: overview.coverage,
        });
        for (const [medium, value] of Object.entries(MEDIUMS_MAPS)) {
          const category =
            data.categories[
              medium as keyof schemas["AudienceExplorerMediumCategories"]
            ];
          if (category) {
            processedMediaMix[value] = category.mediums.map((item) => ({
              ...item,
              audience_coverage: item.coverage.audience,
            }));
            overallMediaMix[value] = {
              all: mapOverview(category.overall),
              political_buying: mapOverview(category.political_buying),
              ad_supported: mapOverview(category.ad_supported),
            };
            selectedByCategory[value] = {
              ...category.selected,
              audience_coverage: category.selected.coverage,
            };
          }
        }
        return {
          categories: processedMediaMix,
          overallByCategory: overallMediaMix,
          selectedByCategory: selectedByCategory,
          overall: data.overall,
        };
      },
      tvRatingsParams(params: any, isCable: boolean = false) {
        return {
          ...params,
          meta_audience_id: metaAudience.value?.id,
          index_meta_audience_id: defaultIndexAudienceId.value,
          geography: geography.value,
          genres: ["All Programming"],
          tv_categories: isCable ? ["Cable"] : [],
          date_ranges: Object.values(params.date_ranges[0]),
          per_page: 25,
          page: 1,
          sort_by: "rating",
          sort_desc: true,
        };
      },
      async checkHouseholdThreshold() {
        tvRatingsError.value = null;
        loading.value.checkHouseholdThreshold = true;
        try {
          const { error } = await client.POST(
            "/api/v1/tv_ratings/check_household_threshold",
            {
              body: {
                params: {
                  meta_audience_id: metaAudience.value?.id,
                  geography: geography.value,
                },
              } as schemas["TvRatingsCheckHouseholdThresholdRequest"],
            },
          );
          if (error) {
            tvRatingsError.value =
              (error as { message?: string }).message ?? null;
          }
        } catch (e: any) {
          tvRatingsError.value = e?.message ?? null;
        } finally {
          loading.value.checkHouseholdThreshold = false;
        }
      },
      async getTopTvPrograms(params: any) {
        if (
          !defaultIndexAudienceId.value ||
          !metaAudience.value ||
          !geography.value
        ) {
          // TODO: show error message
          return;
        }

        loading.value.topTvPrograms = true;
        errors.value.topTvPrograms = undefined;
        try {
          const { data } = await client.POST("/api/v1/tv_ratings/ratings", {
            body: { params } as schemas["TvRatingsRatingsGetRequest"],
          });
          const taskId = (data as unknown as PendingTaskResponse | undefined)
            ?.task_id;
          if (taskId) {
            taskIds.value.topTvPrograms = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.topTvPrograms = false;
          }
        } catch {
          loading.value.topTvPrograms = false;
        }
      },
      create_export(sections: string[], includeUnknownDemos: boolean) {
        if (!metaAudience.value || !geography.value) return Promise.resolve();
        const metaAudienceId = metaAudience.value.id;
        const geographyId = geography.value.id;
        return exportManager.startExport("export", () =>
          client.POST("/api/v1/audience_explorer/export", {
            body: {
              meta_audience_id: metaAudienceId,
              geography_id: geographyId,
              sections,
              include_unknown_demographics: includeUnknownDemos,
              ...comparisonParam.value,
            } as never,
          }),
        );
      },
      exportCounts(count_types: string[], geography_type: string) {
        if (!metaAudience.value || !geography.value) return Promise.resolve();
        const metaAudienceId = metaAudience.value.id;
        const geographyId = geography.value.id;
        return exportManager.startExport(geography_type as ExportKey, () =>
          client.POST("/api/v1/audience_explorer/export_counts", {
            body: {
              meta_audience_id: metaAudienceId,
              geography_id: geographyId,
              count_types,
              geography_type,
            } as unknown as schemas["AudienceExplorerExportCountsRequest"],
          }),
        );
      },
      async getActivationSummary(
        mediums: { meta_audience_id: number; label: string }[],
      ) {
        if (!metaAudience.value || !geography.value) return;
        await cancel("activationSummary");
        loading.value.activationSummary = true;
        const payload = {
          audiences: {
            include: {
              join_type: "overlap",
              join_audiences: [
                {
                  audiences: [
                    {
                      id: metaAudience.value.id,
                      name: metaAudience.value.name,
                    },
                  ],
                },
                {
                  join_type: "union",
                  audiences: mediums.map((medium) => ({
                    id: medium.meta_audience_id,
                    name: medium.label,
                  })),
                },
              ],
            },
          },
          geographies: {
            include: [
              {
                geography_type: geography.value.type,
                ref_id: geography.value.ref_id,
              },
            ],
          },
          skip_demographics: true,
        };
        try {
          const { data } = await client.POST(
            "/api/v1/meta_audiences/audience_preview",
            {
              body: payload as never,
            },
          );
          const taskId = (data as PendingTaskResponse | undefined)?.task_id;
          if (taskId) {
            taskIds.value.activationSummary = taskId;
            $ws?.subscribe({
              channel: "AudienceExplorer",
              payload: { id: taskId },
            });
          } else {
            loading.value.activationSummary = false;
          }
        } catch {
          loading.value.activationSummary = false;
        }
      },
    };
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useAudienceExplorerSearchStore, import.meta.hot),
  );
}
